import { FORM_ERROR } from 'final-form';
import { OK, CREATED } from 'http-status-codes';
import { put, select, takeLatest } from 'redux-saga/effects';

import { formatApiErrors, safeResolveAsync } from '../../utils/saga';
import { loginSaga } from '../auth/login/saga';

import * as api from './api';
import { actions, types } from './duck';
import { preparedRequestData } from './utils';

// --- Business signup (existing, untouched) ---

export function* signUp({ formValues, resolve }) {
  try {
    const marketer_referral_code = yield select(
      state => state.referralCodes.businessCode,
    );

    const patchedData = {
      ...formValues,
      marketer_referral_code,
    };

    yield put(actions.signUpRequest(patchedData));

    const response = yield api.signUp(patchedData);

    if (response.status === CREATED) {
      const { email, password } = patchedData;

      yield loginSaga({ email, password });

      yield put(actions.signUpSuccess());
      yield safeResolveAsync(resolve);
    } else {
      yield put(actions.signUpFailure(response.data));
      yield safeResolveAsync(resolve, formatApiErrors(response.data));
    }
  } catch (error) {
    yield put(actions.signUpFailure(error.message));
    yield safeResolveAsync(resolve, { [FORM_ERROR]: error.message });
  }
}

// --- Customer signup (ported from mobile, prefixed with Customer) ---
// Note: reads customer-scoped values from state.signup.customer.values,
// matching the nested state shape in duck.js (kept separate from the
// business signup's top-level state.signup.values).

export function* customerValidateCustomerInfoSaga({ formValues, resolve }) {
  try {
    const patchedData = preparedRequestData(formValues);

    const response = yield api.validateCustomerInfo(patchedData);

    if (response.status === OK) {
      yield safeResolveAsync(resolve);
    } else {
      yield safeResolveAsync(resolve, formatApiErrors(response.data));
    }
  } catch (error) {
    yield safeResolveAsync(resolve, { [FORM_ERROR]: error.message });
  }
}

export function* customerSignUpSaga({ formValues, resolve }) {
  try {
    const code = yield select(
      state => state.referralCodes.customerCode || null,
    );
    const contactInfoValues = yield select(
      state => state.signup.customer.values || {},
    );

    // Default referral code when none is present — the backend's
    // marketer_referral_code field is required=False but not allow_null,
    // so an explicit null fails validation ("may not be null"). Sending
    // this fallback code instead of null/omitting keeps the field always
    // present and valid.
    const DEFAULT_REFERRAL_CODE = 'vw4-hmd';

    const patchedData = {
      marketer_referral_code: code || DEFAULT_REFERRAL_CODE,
      ...preparedRequestData({
        ...formValues,
        ...contactInfoValues,
      }),
    };

    yield put(actions.customerSignUpRequest(formValues));

    const response = yield api.customerSignUp(patchedData);

    if (response.status === CREATED) {
      const { email, password, firebaseIdToken } = patchedData;
      const { premium_sponsor } = response.data || {};

      yield put(actions.customerSetPremiumSponsor(premium_sponsor));
      yield loginSaga({ email, password, firebaseIdToken });
      yield put(actions.customerSignUpSuccess());
      yield put(actions.customerSetSignupProgress(false));
      yield put(actions.customerSetSignupCompleted(true));
      yield safeResolveAsync(resolve);
    } else {
      yield put(actions.customerSignUpFailure(response.data));
      yield safeResolveAsync(resolve, formatApiErrors(response.data));
    }
  } catch (error) {
    yield put(actions.customerSignUpFailure(error.message));
    yield put(actions.customerSetSignupProgress(false));
    yield put(actions.customerSetSignupCompleted(false));
    yield safeResolveAsync(resolve, { [FORM_ERROR]: error.message });
  }
}

export default function* saga() {
  yield takeLatest(types.SIGN_UP, signUp);

  yield takeLatest(types.CUSTOMER_SIGN_UP, customerSignUpSaga);
  yield takeLatest(
    types.CUSTOMER_VALIDATE_CUSTOMER_INFO,
    customerValidateCustomerInfoSaga,
  );
}